import { forwardRef, useImperativeHandle, useLayoutEffect, useRef } from 'react';
import { Bold, Italic, Underline, List, ListOrdered, Heading2, Heading3, Pilcrow, Undo2 } from 'lucide-react';

// Editor del testo di una sezione del verbale di onboarding: la sezione si modifica com'è
// stampata, tabelle comprese. L'HTML arriva già ripulito dal backend (services/onboarding/
// sections.ts sanitizeDocHtml), che lo ripulisce di nuovo a ogni salvataggio; incollando si
// prende solo il testo, per non portarsi dietro la formattazione di Word. Il componente non
// segue i cambi di `html`: per caricare un altro testo va rimontato con una `key` diversa.

export interface DocHtmlEditorHandle { insertText: (text: string) => void }

interface Props { html: string; onChange: (html: string) => void; disabled?: boolean; minHeight?: number }

const TOOLS = [
  { icon: Bold, label: 'Bold', command: 'bold' },
  { icon: Italic, label: 'Italic', command: 'italic' },
  { icon: Underline, label: 'Underline', command: 'underline' },
  { icon: List, label: 'Bullet list', command: 'insertUnorderedList' },
  { icon: ListOrdered, label: 'Numbered list', command: 'insertOrderedList' },
  { icon: Heading2, label: 'Heading', command: 'formatBlock', value: 'h2' },
  { icon: Heading3, label: 'Subheading', command: 'formatBlock', value: 'h3' },
  { icon: Pilcrow, label: 'Paragraph', command: 'formatBlock', value: 'p' },
  { icon: Undo2, label: 'Undo', command: 'undo' },
];

const DocHtmlEditor = forwardRef<DocHtmlEditorHandle, Props>(function DocHtmlEditor({ html, onChange, disabled, minHeight = 320 }, ref) {
  const box = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (box.current) box.current.innerHTML = html;
    // Solo al montaggio: vedi il commento in testa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => { if (box.current) onChange(box.current.innerHTML); };

  function run(command: string, value?: string) {
    box.current?.focus();
    document.execCommand(command, false, value);
    emit();
  }

  useImperativeHandle(ref, () => ({ insertText: (text: string) => run('insertText', text) }));

  return (
    <div className="border border-gray-200 rounded-md bg-white">
      {!disabled && (
        <div className="flex flex-wrap gap-0.5 border-b border-gray-100 p-1">
          {TOOLS.map(t => (
            <button key={t.label} type="button" title={t.label} aria-label={t.label} className="p-1.5 rounded text-gray-600 hover:bg-gray-100"
              onMouseDown={e => { e.preventDefault(); run(t.command, t.value); }}>
              <t.icon size={14} />
            </button>
          ))}
        </div>
      )}
      <div ref={box} className="doc-editor p-3 overflow-auto" style={{ minHeight }} contentEditable={!disabled} suppressContentEditableWarning
        role="textbox" aria-multiline="true" onInput={emit}
        onPaste={e => { e.preventDefault(); document.execCommand('insertText', false, e.clipboardData.getData('text/plain')); emit(); }} />
    </div>
  );
});

export default DocHtmlEditor;
